import React, { memo, useCallback, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { NavigationBar } from './DesignSystem';
import { getNavItemsForRole, getNavItemsExcludingMobileMain } from '../utils/navigationUtils';
import { getUserInitials as getInitials, getUserDisplayName, getPremiumBadge } from '../utils/userDisplay';

const MobileLayout: React.FC<{ children: React.ReactNode }> = memo(({ children }) => {

  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, /* toggleTheme, */ getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleMenuToggle = () => setMenuOpen((v) => !v);

  const getNavItems = () => {
    if (!user) return [];
    return getNavItemsExcludingMobileMain(user.role);
  };

     return (
    <div
      className="mobile-layout theme-shell animate-fade-in"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: themeStyles.backgroundGradient,
        color: themeStyles.text,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
  <div className="layout-glow layout-glow--indigo animate-float" aria-hidden="true" />
  <div className="layout-glow layout-glow--cyan animate-float" aria-hidden="true" />
      {/* Header Mobile Premium */}
      <header
        className="header-mobile safe-area-top animate-fade-in"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1001,
          background: '#ffffff',
          backdropFilter: 'blur(12px) saturate(140%)',
          WebkitBackdropFilter: 'blur(12px) saturate(140%)',
          borderBottom: `1px solid rgba(2,6,23,0.08)`,
          boxShadow: '0 6px 16px rgba(2,6,23,0.06)',
          padding: '0.85rem 1.25rem',
          minHeight: 'var(--header-height)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}
      >
  {/* Logo Grented */}
  <BrandLogo height={84} />

        {/* Pulsante Accedi/Registrati per utenti non autenticati */}
        {!token && (
          <Link
            to="/login"
            style={{
              padding: '0.5rem 1.5rem',
              background: '#1d4ed8',
              border: '1px solid rgba(2,6,23,0.08)',
              borderRadius: '20px',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: '600',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 20px rgba(29,78,216,0.25)',
              whiteSpace: 'nowrap'
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = 'scale(0.97)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(29,78,216,0.25)';
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 20px rgba(29,78,216,0.25)';
            }}
          >
            Accedi/Registrati
          </Link>
        )}

        {/* Avatar and controls (avatar) */}
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '22px',
              background: themeStyles.accentGradient,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.85rem',
              fontWeight: '700',
              cursor: 'pointer',
              border: `1px solid rgba(255, 255, 255, 0.18)`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              boxShadow: themeStyles.glow
            }}
            onClick={() => navigate('/profile')}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}>
              {getInitials(user, user)}
            </div>
          </div>
        )}
      </header>

      {/* Overlay scuro per chiusura menu */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(4, 7, 21, 0.65)',
            zIndex: 1199,
            transition: 'opacity 0.3s ease',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />
      )}

      {/* Side menu mobile premium */}
      <nav
        className="side-menu-mobile"
        style={{
          position: 'fixed',
          bottom: 0, // Ancorato in basso invece che in alto
          right: menuOpen ? 0 : '-85vw',
          width: '85vw',
          maxWidth: 340,
          maxHeight: '85vh', // Ridotto per non occupare tutto lo schermo
          background: themeStyles.cardGlass,
          boxShadow: menuOpen ? themeStyles.cardShadow : 'none',
          zIndex: 1200,
          transition: 'right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          borderTopLeftRadius: 38,
          borderTopRightRadius: 38, // Manteniamo i bordi arrotondati in alto
          borderBottomLeftRadius: 38,
          borderBottomRightRadius: 38,
          pointerEvents: menuOpen ? 'auto' : 'none',
          border: `1px solid ${themeStyles.border}`,
          backdropFilter: 'blur(26px) saturate(165%)',
          WebkitBackdropFilter: 'blur(26px) saturate(165%)',
          display: 'flex',
          flexDirection: 'column'
        }}
        {...(!menuOpen ? { inert: 'true' } : {})}
      >
        {/* Header del menu */}
        <div
        onClick={() => setMenuOpen(false)}
        style={{
          padding: '1.5rem 1.5rem 2rem',
          borderBottom: `1px solid ${themeStyles.border}`,
          background: themeStyles.accentGradient,
          borderTopLeftRadius: 38,
          borderTopRightRadius: 38
        }}>
          {user && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '25px',
                background: 'rgba(255, 255, 255, 0.22)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                fontWeight: '700',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 8px 22px -10px rgba(255, 255, 255, 0.35)'
              }}>
                {getInitials(user, user)}
              </div>
              <div>
                <div style={{ 
                  color: 'white', 
                  fontWeight: '700', 
                  fontSize: '1.1rem' 
                }}>
                  {getUserDisplayName(user, user)} {getPremiumBadge(user)}
                </div>
                <div style={{ 
                  color: 'rgba(255, 255, 255, 0.8)', 
                  fontSize: '0.85rem',
                  textTransform: 'capitalize'
                }}>
                  {user.role}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Logout - spostato in alto dopo l'header */}
        <div style={{
          padding: '1rem',
          borderBottom: `1px solid ${themeStyles.border}`
        }}>
          <button
            onClick={() => {
              handleLogout();
              setMenuOpen(false);
            }}
            style={{
              width: '90%',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              margin: '0.4rem 1rem',
              background: 'linear-gradient(135deg, #f87171 0%, #ef4444 45%, #b91c1c 100%)',
              border: '1px solid rgba(248, 113, 113, 0.45)',
              borderRadius: '24px',
              color: 'white',
              fontSize: '1.2rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 16px 32px -18px rgba(248, 113, 113, 0.65)'
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f87171 0%, #ef4444 45%, #b91c1c 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Icon name="logout" size={24} />
            <span>Logout</span>
          </button>
        </div>

        {/* Navigation items - spostati in basso */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '1rem',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling su iOS
          scrollbarWidth: 'none', // Nasconde scrollbar Firefox
          msOverflowStyle: 'none' // Nasconde scrollbar IE/Edge
        }}
        className="mobile-menu-scroll">
          {getNavItems().map((item, index) => (
            <button
              key={index}
              onClick={() => {
                navigate(item.path);
                setMenuOpen(false);
              }}
              style={{
                width: '90%',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                margin: '.4rem 1rem',
                background: 'rgba(255, 255, 255, 0.08)',
                border: `1px solid ${themeStyles.border}`,
                borderRadius: '24px',
                color: themeStyles.text,
                fontSize: '1.3rem',
                fontWeight: '450',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 12px 32px -20px rgba(6, 182, 212, 0.45)'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.16)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <span style={{ fontSize: '1.5rem' }}><Icon name={item.icon} size={24} /></span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Menu chiuso automaticamente */}
      </nav>

      {/* Main Content */}
      <main
        className="main-content scrollable"
        style={{
          flex: 1,
          paddingTop: 'calc(var(--header-height) + 1.5rem)',
          paddingBottom: 150, // Ridotto per singola riga di navigazione
          paddingLeft: '1.25rem',
          paddingRight: '1.25rem',
          background: 'transparent',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center'
        }}
      >
        <div
          className="container glass-card animate-fade-in-up"
          style={{
            width: '100%',
            maxWidth: 960,
            background: themeStyles.cardGlass,
            border: `1px solid ${themeStyles.border}`,
            boxShadow: themeStyles.cardShadow,
            padding: '1.5rem',
            borderRadius: '28px',
            backdropFilter: 'blur(24px) saturate(165%)',
            WebkitBackdropFilter: 'blur(24px) saturate(165%)'
          }}
        >
          {children}
        </div>
      </main>

      {/* Floating Menu Button - positioned above profile icon in bottom nav */}
      {user && (
        <button
          className="animate-glow-pulse"
          aria-label="Apri menu"
          onClick={handleMenuToggle}
          style={{
            position: 'fixed',
            bottom: '140px', // Adjusted for single row navigation
            right: '8px', // Aligned with the right edge of first bottom nav button
            width: 56,
            height: 56,
            borderRadius: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: themeStyles.accentGradient,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: themeStyles.buttonText ?? '#041029',
            cursor: 'pointer',
            zIndex: 1099, // Below nav (1100) but above content
            boxShadow: themeStyles.glow,
            transition: 'all 0.3s ease'
          }}
          onTouchStart={(e) => {
            e.currentTarget.style.transform = 'scale(0.95)';
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ width: '20px', height: '2px', background: '#ffffff', borderRadius: '1px', transform: menuOpen ? 'rotate(45deg) translateY(6px)' : 'rotate(0)', transition: 'all 0.3s ease' }} />
            <div style={{ width: '20px', height: '2px', background: '#ffffff', borderRadius: '1px', opacity: menuOpen ? 0 : 1, transition: 'all 0.3s ease' }} />
            <div style={{ width: '20px', height: '2px', background: '#ffffff', borderRadius: '1px', transform: menuOpen ? 'rotate(-45deg) translateY(-6px)' : 'rotate(0)', transition: 'all 0.3s ease' }} />
          </div>
        </button>
      )}

      {/* Bottom Navigation Mobile Premium */}
      {user && (
        <NavigationBar variant="mobile" />
      )}

      {/* For guest users (no auth) show two bottom tiles for quick search
          These mimic the style and placement of the mobile NavigationBar,
          but are limited to: Cerca proprietà, Cerca inquilini */}
      {!user && (
        <nav className={`nav-mobile guest-nav-mobile`} style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: themeStyles.cardBackground,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: `1px solid ${themeStyles.border}`,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1100,
          boxShadow: `0 -4px 32px ${themeStyles.shadow}`,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '12px 8px'
        }}>
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            {/* Cerca proprietà */}
            <button
              className="nav-mobile-item touch-target"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                borderRadius: 12,
                padding: '12px 8px',
                minHeight: 64,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeStyles.textSecondary,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/search')}
            >
              <span style={{ fontSize: 22, marginBottom: 4 }}><Icon name="search" size={22} /></span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Cerca proprietà</span>
            </button>

            {/* Cerca inquilini */}
            <button
              className="nav-mobile-item touch-target"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                borderRadius: 12,
                padding: '12px 8px',
                minHeight: 64,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeStyles.textSecondary,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/tenant-search')}
            >
              <span style={{ fontSize: 22, marginBottom: 4 }}><Icon name="tenant" size={22} /></span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Cerca inquilini</span>
            </button>
          </div>
        </nav>
      )}

      {/* Inline styles for mobile layout */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .mobile-layout { 
            position: relative; 
            min-height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .main-content { 
            padding-left: 0; 
            padding-right: 0; 
          }
          @media (min-width: 768px) {
            .main-content { 
              padding-left: 1rem; 
              padding-right: 1rem; 
            }
          }
          .touch-target {
            min-height: 44px;
            min-width: 44px;
          }
          /* Safe area support for notched devices */
          .safe-area-top {
            padding-top: env(safe-area-inset-top);
          }
          .safe-area-bottom {
            padding-bottom: env(safe-area-inset-bottom);
          }
          /* Smooth scrolling */
          .scrollable {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
          /* Prevent text selection on touch */
          .nav-mobile-item {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            -webkit-touch-callout: none;
          }
          /* Hide scrollbar for mobile menu */
          .mobile-menu-scroll::-webkit-scrollbar {
            display: none;
          }
        `
      }} />
    </div>
  );
});

export default MobileLayout;
